import { faEye, faPenToSquare, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DataGrid, { Column, Pager, Paging, Scrolling, SearchPanel, Summary, TotalItem } from 'devextreme-react/cjs/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import useHasPermission from 'hooks/useHasPermission';
import React, { useRef, useState } from 'react'
import { Card } from 'react-bootstrap';
import { getpoInwardAndInventory } from '../services/po.stock.service';
import RejectProduct from './RejectProduct';
import { useNavigate } from 'react-router-dom';
import { encryptItem } from 'helpers/secureNavigate';

const POInward = () => {
    const HasPermission = useHasPermission()
    const [refreshKey, setRefreshKey] = useState(0);
    const navigate = useNavigate();

    const rejectModalRef = useRef<any>(null);

    const reloadDataSource = () => {
        setRefreshKey((prev) => prev + 1);
    };

    const dataSource = (): any | undefined => {
        return new CustomStore({
            key: "id",
            load: async (loadOptions) => {
                try {
                    const { searchValue } = loadOptions;
                    const parameterList = new URLSearchParams();
                    const search = JSON.parse(JSON.stringify(loadOptions));

                    for (const [key, value] of Object.entries(search)) {
                        parameterList.append(key, JSON.stringify(value));
                    }

                    if (searchValue) {
                        parameterList.append("search", searchValue);
                    }

                    parameterList.append("isRecieved", "false");

                    const response = await getpoInwardAndInventory(parameterList);
                    return response;
                } catch (error) {
                    console.error("Data loading error:", error);
                    return { data: [], totalCount: 0 };
                }
            },
        });
    };

    const handleNavigate = (id: any, productName: any, materialType: any) => {
        try {
            if (!id) {
                console.error('No ID provided');
                return;
            }

            const encryptedId = encryptItem(id);
            const encryptedMaterialType = encryptItem(materialType);
            if (!encryptedId || !encryptedMaterialType) {
                console.error('Encryption failed');
                return;
            }

            const safeProductName = encodeURIComponent(productName || 'product');
            const targetUrl = `/po-inward/edit/${encryptedId}/${safeProductName}/${encryptedMaterialType}`;

            navigate(targetUrl, { replace: true });

        } catch (error) {
            console.error('Navigation error:', error);
        }
    }

    return (
        <>
            <div className="page-content">
                <div className="container-fluid">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="m-0">PO Inward</h5>
                        </Card.Header>
                        <Card.Body>
                            {HasPermission("ItemMaster.Read") ? (
                                <DataGrid
                                    dataSource={dataSource()}
                                    remoteOperations={{ groupPaging: true }}
                                    showRowLines={true}
                                    keyExpr="id"
                                    key={refreshKey}
                                    showBorders={true}
                                    columnAutoWidth={true}
                                    onContentReady={(e) => {
                                        const hasData = e.component.totalCount() > 0;
                                        e.component.option("pager.visible", hasData);
                                        e.component.option("paging.visible", hasData);
                                    }}
                                >
                                    <Pager
                                        showPageSizeSelector
                                        allowedPageSizes={[10, 25, 50, 100]}
                                        showNavigationButtons
                                        displayMode="full"
                                        showInfo
                                    />

                                    <Scrolling
                                        mode="standard"
                                        useNative={true}
                                        scrollByContent={true}
                                        scrollByThumb={true}
                                    />

                                    <SearchPanel
                                        visible={true}
                                        width={240}
                                        placeholder="Search..."
                                    />

                                    <Summary>
                                        <TotalItem />
                                    </Summary>

                                    <Paging defaultPageSize={10} />

                                    {HasPermission("ItemMaster.Write") && (
                                        <Column
                                            allowFiltering={false}
                                            caption="Action"
                                            cssClass="text-center"
                                            cellRender={({ data }) => (
                                                <button
                                                    className="btn btn-sm action-btn edit-btn"
                                                    title="Product QC"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleNavigate(data?.id, data?.productName, data?.materialType)
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faPenToSquare} className="ml-2" />
                                                </button>
                                            )}
                                        />
                                    )}

                                    <Column dataField="id" dataType="number" sortOrder="desc" width={150} visible={false} />
                                    <Column dataField="poInvoiceNo" caption="Invoice No." dataType="string" />
                                    <Column dataField="productName" caption="Product Name" dataType="string" />
                                    <Column dataField="styleNo" caption="Style No" dataType="string" />
                                    <Column dataField="materialType" caption="Material Type" dataType="string" />
                                    <Column dataField="karat" caption="Karat" dataType="string" />
                                    <Column dataField="standardGoldWeight" caption="Std Gold Wt" format="#,##0.000" />
                                    <Column dataField="standardDiamondWeight" caption="Std Diamond Wt" format="#,##0.000" />
                                    <Column dataField="expectedGoldRate" caption="Expected Gold Rate" format="#,##0.000" />
                                    <Column dataField="qty" caption="Quantity" dataType="number" />
                                    <Column dataField="status" caption="Status" dataType="string" />
                                    <Column dataField="isRecieved" caption="Received" cellRender={({ data }) => <span>{data.isRecieved ? 'Yes' : 'No'}</span>} />
                                    <Column dataField="isSRP" caption="Is SRP" cellRender={({ data }) => <span>{data.isSRP ? 'Yes' : 'No'}</span>} />
                                    <Column dataField="actualGoldWeight" caption="Actual Gold Wt" format="#,##0.000" />
                                    <Column dataField="actualDiamondWeight" caption="Actual Diamond Wt" format="#,##0.000" />
                                    <Column dataField="craftsmanshipCost" caption="Craftsmanship Rate" format="#,##0.00" />
                                    <Column dataField="goldCost" caption="Gold Rate" format="#,##0.00" />
                                    <Column dataField="diamondCost" caption="Diamond Rate" format="#,##0.00" />
                                    <Column dataField="makingCharges" caption="Making Charges" format="#,##0.00" />
                                    <Column dataField="otherCharges" caption="Other Charges" format="#,##0.00" />
                                    <Column dataField="totalCost" caption="Selling Price" format="#,##0.00" />
                                    <Column dataField="dpPrice" caption="DP Price" format="#,##0.00" />
                                    <Column dataField="bagNumber" caption="Bag Number" />
                                    <Column dataField="specialInstructions" caption="Special Instructions" width={250} />
                                </DataGrid>
                            ) : null}
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <RejectProduct
                ref={rejectModalRef}
                reloadDataSource={reloadDataSource}
            />
        </>
    )
}

export default POInward;